import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

const BUBBLE_WIDTH = 240;
const VIEWPORT_GUTTER = 8;

/**
 * Petit bouton « ? » qui ouvre une bulle d'explication au clic.
 * La bulle est en `position: fixed` (calculée depuis le bouton) pour ne pas
 * être rognée par un panneau à défilement. Se ferme au clic ailleurs ou Échap.
 */
export function InfoTip({ label, children }: {
  /** Nom de ce qui est expliqué, pour les lecteurs d'écran (« Aide : Au sol »). */
  label: string;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const bubbleId = useId();

  useEffect(() => {
    if (!pos) return;
    const close = (e: PointerEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || bubbleRef.current?.contains(target)) return;
      setPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setPos(null); buttonRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [pos]);

  const toggle = () => {
    if (pos) { setPos(null); return; }
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxLeft = window.innerWidth - BUBBLE_WIDTH - VIEWPORT_GUTTER;
    setPos({ top: r.bottom + 6, left: Math.max(VIEWPORT_GUTTER, Math.min(r.left - 12, maxLeft)) });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="pl-infotip"
        aria-label={`Aide : ${label}`}
        aria-expanded={pos !== null}
        aria-controls={pos ? bubbleId : undefined}
        onClick={(e) => { e.stopPropagation(); toggle(); }}
      >
        ?
      </button>
      {pos && (
        <div ref={bubbleRef} id={bubbleId} role="tooltip" className="pl-infotip-bubble" style={{ top: pos.top, left: pos.left, width: BUBBLE_WIDTH }}>
          {children}
        </div>
      )}
    </>
  );
}
